import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  fetchWorkers,
  createWorker,
  updateWorker,
  deleteWorker,
} from "../api/workerApi";
import "../styles/ManageWorkers.css";

/* Helper — initials from full name */
const getInitials = (name = "") => {
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0] || "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* ════════════════════════════════════════
   DROPDOWN OPTIONS
════════════════════════════════════════ */
const FARM_OPTIONS = ["RVR 1", "RVR 2"];
const VILLAGE_OPTIONS = ["Village A", "Village B", "Village C"];

const ManageWorkers = () => {
  const [workers, setWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    payroll_number: "",
    full_name: "",
    farm: "",
    village: "",
  });

  // =====================
  // LOAD
  // =====================
  const loadWorkers = async () => {
    try {
      const data = await fetchWorkers();
      setWorkers(data);
    } catch {
      Swal.fire("Error", "Failed to load workers", "error");
    }
  };

  useEffect(() => {
    loadWorkers();
  }, []);

  // =====================
  // FORM
  // =====================
  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const resetForm = () => {
    setEditingItem(null);
    setForm({ payroll_number: "", full_name: "", farm: "", village: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateWorker(editingItem.id, form);
        Swal.fire("Updated!", "Worker updated successfully", "success");
      } else {
        await createWorker(form);
        Swal.fire("Created!", "Worker added successfully", "success");
      }
      loadWorkers();
      resetForm();
    } catch {
      Swal.fire("Error", "Operation failed", "error");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({ ...item });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =====================
  // DELETE
  // =====================
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this worker?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#a8a49a",
      confirmButtonText: "Yes, delete it",
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteWorker(id);
      loadWorkers();
      Swal.fire("Deleted!", "Record removed successfully", "success");
    } catch {
      Swal.fire("Error", "Failed to delete record", "error");
    }
  };

  // =====================
  // FILTER
  // =====================
  const filteredWorkers = workers.filter((w) => {
    const term = searchTerm.toLowerCase();
    return (
      w.payroll_number?.toLowerCase().includes(term) ||
      w.full_name?.toLowerCase().includes(term) ||
      w.farm?.toLowerCase().includes(term) ||
      w.village?.toLowerCase().includes(term)
    );
  });

  // =====================
  // RENDER
  // =====================
  return (
    <div className="mw-root">

      {/* ── Page Header ── */}
      <div className="mw-header">
        <h1 className="mw-header-title">
          Manage <span>Bunchures</span>
        </h1>
        <span className="mw-header-badge">
          {workers.length} {workers.length === 1 ? "Worker" : "Workers"}
        </span>
      </div>

      <div className="mw-layout">

        {/* ── Left Panel: Form ── */}
        <div className="mw-card">
          <div className="mw-card-header">
            <div className="mw-card-icon mw-card-icon--rose">👷</div>
            <div>
              <p className="mw-card-title">
                {editingItem ? "Edit Bunchure" : "New Bunchure"}
              </p>
              <p className="mw-card-subtitle">
                {editingItem
                  ? `Editing: ${editingItem.full_name}`
                  : "Register a new Bunchure"}
              </p>
            </div>
          </div>

          <div className="mw-card-body">
            {editingItem && (
              <div className="mw-edit-banner" style={{ marginBottom: 16 }}>
                <span>✏️</span>
                <span>
                  Editing <strong>{editingItem.full_name}</strong>
                </span>
              </div>
            )}

            <form className="mw-form" onSubmit={handleSubmit}>
              <p className="mw-section-label">Identity</p>

              {/* Payroll + Name */}
              <div className="mw-field">
                <label className="mw-label">
                  Payroll Number <span className="mw-required">*</span>
                </label>
                <input
                  className="mw-input"
                  name="payroll_number"
                  placeholder="e.g. EMP-0042"
                  value={form.payroll_number}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mw-field">
                <label className="mw-label">
                  Full Name <span className="mw-required">*</span>
                </label>
                <input
                  className="mw-input"
                  name="full_name"
                  placeholder="e.g. Ngoma Allan"
                  value={form.full_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mw-divider" />
              <p className="mw-section-label">Location</p>

              <div className="mw-field-row">

                {/* ── Farm Dropdown ── */}
                <div className="mw-field">
                  <label className="mw-label">Farm</label>
                  <select
                    className="mw-input"
                    name="farm"
                    value={form.farm}
                    onChange={handleChange}
                  >
                    <option value="">— Select Farm —</option>
                    {FARM_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ── Village Dropdown ── */}
                <div className="mw-field">
                  <label className="mw-label">Village</label>
                  <select
                    className="mw-input"
                    name="village"
                    value={form.village}
                    onChange={handleChange}
                  >
                    <option value="">— Select Village —</option>
                    {VILLAGE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

              </div>

              <div className="mw-btn-row">
                <button type="submit" className="mw-btn mw-btn--primary">
                  {editingItem ? "✓ Update Worker" : "+ Add Worker"}
                </button>
                {editingItem && (
                  <button
                    type="button"
                    className="mw-btn mw-btn--ghost"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* ── Right Panel: Table ── */}
        <div className="mw-card">
          <div className="mw-card-header">
            <div className="mw-card-icon mw-card-icon--olive">📋</div>
            <div>
              <p className="mw-card-title">All Bunchures</p>
              <p className="mw-card-subtitle">
                Search, edit and manage Bunchures
              </p>
            </div>
          </div>

          {/* Toolbar / Search */}
          <div className="mw-toolbar">
            <div className="mw-input-wrap">
              <span className="mw-input-icon">🔍</span>
              <input
                className="mw-input"
                type="text"
                placeholder="Search by payroll, name, farm or village…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="mw-input-clear"
                  onClick={() => setSearchTerm("")}
                  type="button"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {searchTerm && (
              <span className="mw-result-count">
                {filteredWorkers.length} result
                {filteredWorkers.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="mw-table-wrapper">
            <table className="mw-table">
              <thead>
                <tr>
                  <th>Payroll</th>
                  <th>Name</th>
                  <th>Farm</th>
                  <th>Village</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkers.length > 0 ? (
                  filteredWorkers.map((w) => (
                    <tr key={w.id}>
                      <td>
                        <span className="mw-payroll-badge">
                          {w.payroll_number}
                        </span>
                      </td>
                      <td>
                        <div className="mw-name-cell">
                          <div className="mw-avatar">
                            {getInitials(w.full_name)}
                          </div>
                          <span className="mw-name-text">{w.full_name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="mw-tag mw-tag--farm">
                          {w.farm || <span style={{ color: "var(--neutral-400)" }}>—</span>}
                        </span>
                      </td>
                      <td>
                        <span className="mw-tag">
                          {w.village || <span style={{ color: "var(--neutral-400)" }}>—</span>}
                        </span>
                      </td>
                      <td>
                        <div className="mw-table-actions">
                          <button
                            className="mw-btn mw-btn--edit"
                            onClick={() => handleEdit(w)}
                          >
                            Edit
                          </button>
                          <button
                            className="mw-btn mw-btn--danger-ghost"
                            onClick={() => handleDelete(w.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <div className="mw-empty-state">
                        <div className="mw-empty-icon">
                          {searchTerm ? "🔍" : "👷"}
                        </div>
                        <p className="mw-empty-text">
                          {searchTerm
                            ? `No results for "${searchTerm}"`
                            : "No workers found"}
                        </p>
                        <p className="mw-empty-sub">
                          {searchTerm
                            ? "Try a different search term"
                            : "Add your first worker using the form"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ManageWorkers;